import { ZERO } from "../../../../../helpers/numbers"
import {
	DATA_DELAY_IN_DAYS,
	MONTH_QUARTER_DELTA,
	QUARTER_INITIAL_MONTHS,
	TOTAL_MONTH_IN_QUARTER,
} from "../../constants"
import {
	addDays,
	addMonths,
	addYears,
	getMonth,
	previousMonday,
	startOfDay,
	startOfQuarter,
	subDays,
	subMonths,
	subYears,
} from "date-fns"
import { getDiffInDays } from "../../../../../helpers/temporal"
import {
	DefinedRangeType,
	RangeCalendar,
	RangeDate,
} from "../../MultiRangeCalendar.domain"
import { COLOR_PALETTE } from "colors"

export const calculatePreviousQuarterAndYear = (currentQuarter: number) => {
	let previousQuarter = currentQuarter
	let previousYear = ZERO

	if (currentQuarter - 1 <= ZERO) {
		previousQuarter = 4
		previousYear--
	} else {
		previousQuarter--
	}

	return [previousQuarter, previousYear]
}

export const getToday = () => addDays(new Date(), -DATA_DELAY_IN_DAYS)

export const getStartQuarterDate = (quarter: number) => {
	return QUARTER_INITIAL_MONTHS[quarter - 1]
}

export const getCurrentQuarter = () => {
	const today = getToday()
	const month = getMonth(today)
	return Math.ceil((month + MONTH_QUARTER_DELTA) / TOTAL_MONTH_IN_QUARTER)
}

export const setZeroDateTimes = (dates: Date[]) => {
	dates.forEach((date) => {
		date.setHours(0)
		date.setMinutes(0)
		date.setSeconds(0)
		date.setMilliseconds(0)
	})
}

const getColorAndLabel = [
	{
		color: COLOR_PALETTE.DARK_PURPLE_600,
		secondaryColor: COLOR_PALETTE.PURPLE_100,
		label:
			"phronesis.transactional.application.commercial.benchmark.current.date",
	},
	{
		color: COLOR_PALETTE.YELLOW_500,
		secondaryColor: COLOR_PALETTE.YELLOW_100,
		label:
			"phronesis.transactional.application.commercial.benchmark.previous.date",
	},
]

const generateRanges = (dates: RangeDate[]): RangeCalendar[] => {
	return dates?.map((date: RangeDate, index) => {
		return {
			id: String(index + 1),
			startDate: date[0],
			endDate: date[1],
			...getColorAndLabel[index],
		}
	})
}

export const presetCustomRanges = () => {
	return generateRanges([
		[null, null],
		[null, null],
	])
}

export const presetWeekPeriod = (): RangeCalendar[] => {
	const today = getToday()
	const currentWeekDay = previousMonday(today)
	const previousWeekDay = previousMonday(currentWeekDay)
	setZeroDateTimes([currentWeekDay, previousWeekDay])

	return generateRanges([
		[currentWeekDay, addDays(currentWeekDay, 6)],
		[previousWeekDay, addDays(previousWeekDay, 6)],
	])
}

export const presetMonthPeriod = (): RangeCalendar[] => {
	const today = getToday()
	const firstDayCurrentMonth = new Date(
		today.getFullYear(),
		today.getMonth(),
		1
	)
	const firstDayPreviousMonth = new Date(
		today.getFullYear(),
		today.getMonth() - 1,
		1
	)

	const currentMonthDays = getDiffInDays(today, firstDayCurrentMonth)
	const previousMonthDays = getDiffInDays(
		addMonths(today, -1),
		firstDayPreviousMonth
	)

	return generateRanges([
		[firstDayCurrentMonth, addDays(firstDayCurrentMonth, currentMonthDays)],
		[firstDayPreviousMonth, addDays(firstDayPreviousMonth, previousMonthDays)],
	])
}

export const presetQuarterPeriod = (): RangeCalendar[] => {
	const currentQuarterNumber = getCurrentQuarter()

	const [previousQuarterNumber, previousYear] = calculatePreviousQuarterAndYear(
		currentQuarterNumber
	)

	const currentQuarterFirstMonth = getStartQuarterDate(currentQuarterNumber)
	const previousQuarterFirstMonth = getStartQuarterDate(previousQuarterNumber)
	const quarterMonthDiff = previousQuarterFirstMonth - currentQuarterFirstMonth

	const previousEndDate = addYears(
		addMonths(getToday(), quarterMonthDiff),
		previousYear
	)

	const startCurrentPeriod = new Date(
		getToday().getFullYear(),
		currentQuarterFirstMonth
	)

	let peviousPeriodYear = 0
	currentQuarterFirstMonth === 0
		? (peviousPeriodYear = getToday().getFullYear() - 1)
		: (peviousPeriodYear = getToday().getFullYear())

	const startPreviousPeriod = new Date(
		peviousPeriodYear,
		previousQuarterFirstMonth
	)

	return generateRanges([
		[
			startCurrentPeriod,
			addDays(
				startCurrentPeriod,
				getDiffInDays(getToday(), startCurrentPeriod)
			),
		],
		[
			startPreviousPeriod,
			addDays(
				startPreviousPeriod,
				getDiffInDays(previousEndDate, startPreviousPeriod)
			),
		],
	])
}

export const presetSemesterPeriod = (): RangeCalendar[] => {
	const currentMonth = getMonth(getToday())
	const semestersMonth = [0, 5]
	const semester = Math.ceil((currentMonth + 1) / 6)

	const startCurrentPeriod = new Date(
		getToday().getFullYear(),
		semestersMonth[semester - 1],
		1
	)
	const startPreviousPeriod = addMonths(startCurrentPeriod, -5)
	const endPreviousPeriod = addMonths(getToday(), -7)

	return generateRanges([
		[
			startCurrentPeriod,
			addDays(
				startCurrentPeriod,
				getDiffInDays(getToday(), startCurrentPeriod)
			),
		],
		[
			startPreviousPeriod,
			addDays(
				startPreviousPeriod,
				getDiffInDays(endPreviousPeriod, startPreviousPeriod)
			),
		],
	])
}

export const presetYearPeriod = (): RangeCalendar[] => {
	const today = getToday()
	const startCurrentPeriod = new Date(today.getFullYear(), 0, 1)
	const startPreviousPeriod = addYears(startCurrentPeriod, -1)
	const endPreviousPeriod = addYears(today, -1)

	return generateRanges([
		[
			startCurrentPeriod,
			addDays(startCurrentPeriod, getDiffInDays(today, startCurrentPeriod)),
		],
		[
			startPreviousPeriod,
			addDays(
				startPreviousPeriod,
				getDiffInDays(endPreviousPeriod, startPreviousPeriod)
			),
		],
	])
}

export const presetMobileYearPeriod = (): RangeCalendar[] => {
	const today = getToday()
	const oneYearAgo = addYears(today, -1)
	const twoYearsAgo = addYears(oneYearAgo, -1)

	return generateRanges([
		[oneYearAgo, addDays(oneYearAgo, getDiffInDays(today, oneYearAgo))],
		[twoYearsAgo, addDays(twoYearsAgo, getDiffInDays(oneYearAgo, twoYearsAgo))],
	])
}

export const getPresetDefault = (ranges: DefinedRangeType[], id: string) => {
	return ranges.find((range) => range.id === id)?.fn()
}

export const presetQuarterLastYear = (): RangeCalendar[] => {
	const now = getToday()
	const currentQuarterStart = startOfQuarter(now)
	const currentQuarterToDate = now
	const lastYearQuarterStart = subYears(currentQuarterStart, 1)
	const lastYearQuarterToDate = subYears(currentQuarterToDate, 1)

	return generateRanges([
		[currentQuarterStart, currentQuarterToDate],
		[lastYearQuarterStart, lastYearQuarterToDate],
	])
}

export const presetMobileQuarterVsLastYear = (): RangeCalendar[] => {
	const now = getToday()
	const endDate = startOfDay(now)
	const startDate = subMonths(endDate, 3)
	const previousEndDate = subYears(endDate, 1)
	const previousStartDate = subYears(startDate, 1)

	return generateRanges([
		[startDate, endDate],
		[previousStartDate, previousEndDate],
	])
}

export const presetMonthVsLastYear = (): RangeCalendar[] => {
	const currentDate = getToday()
	const startOfCurrentMonth = new Date(
		currentDate.getFullYear(),
		currentDate.getMonth(),
		1
	)

	const startOfLastYearMonth = new Date(
		currentDate.getFullYear() - 1,
		currentDate.getMonth(),
		1
	)

	const sameDayLastYear = new Date(
		currentDate.getFullYear() - 1,
		currentDate.getMonth(),
		currentDate.getDate()
	)

	return generateRanges([
		[startOfCurrentMonth, currentDate],
		[startOfLastYearMonth, sameDayLastYear],
	])
}

export const presetMobileMonthVsLastYear = () => {
	const now = getToday()
	const startOfLast30Days = subDays(now, 30)
	const endOfLast30Days = now

	const startOfLastYear30Days = new Date(
		startOfLast30Days.getFullYear() - 1,
		startOfLast30Days.getMonth(),
		startOfLast30Days.getDate()
	)
	const endOfLastYear30Days = new Date(
		endOfLast30Days.getFullYear() - 1,
		endOfLast30Days.getMonth(),
		endOfLast30Days.getDate()
	)

	return generateRanges([
		[startOfLast30Days, endOfLast30Days],
		[startOfLastYear30Days, endOfLastYear30Days],
	])
}
